import { type FC, useState } from "react";
import { useGeoTemplateActions, useGeoTemplates } from "../useGeoSearch";
import { useGeoTargeting } from "../useGeoTargeting";

export const GeoTemplateManager: FC = () => {
	const { geo, applyTemplate } = useGeoTargeting();
	const { data: templates = [], isLoading, isError } = useGeoTemplates();
	const { createTemplate, deleteTemplate } = useGeoTemplateActions();
	const [saving, setSaving] = useState(false);
	const [templateName, setTemplateName] = useState("");
	const [showSaveForm, setShowSaveForm] = useState(false);

	const handleSave = () => {
		if (!templateName.trim()) return;
		setSaving(true);
		createTemplate.mutate(
			{
				name: templateName.trim(),
				geoTargeting: geo,
			},
			{
				onSuccess: () => {
					setTemplateName("");
					setShowSaveForm(false);
					setSaving(false);
				},
				onError: () => setSaving(false),
			},
		);
	};

	const handleLoad = (id: number) => {
		const template = templates.find((t) => t.id === id);
		if (template) {
			applyTemplate(template);
		}
	};

	const handleDelete = (id: number) => {
		deleteTemplate.mutate(id);
	};

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: 6,
				border: "1px solid #dee2e6",
				borderRadius: 6,
				padding: 8,
			}}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<span style={{ fontSize: 13, fontWeight: 500 }}>Geo Templates</span>
				<button
					type="button"
					className="btn btn-sm btn-outline-secondary"
					style={{ fontSize: 12 }}
					onClick={() => setShowSaveForm(!showSaveForm)}
				>
					{showSaveForm ? "Cancel" : "Save as Template"}
				</button>
			</div>

			{showSaveForm && (
				<div style={{ display: "flex", gap: 4 }}>
					<input
						type="text"
						className="form-control form-control-sm"
						placeholder="Template name..."
						value={templateName}
						onChange={(e) => setTemplateName(e.target.value)}
						style={{ fontSize: 12 }}
					/>
					<button
						type="button"
						className="btn btn-sm btn-primary"
						onClick={handleSave}
						disabled={saving || !templateName.trim()}
						style={{ fontSize: 12, whiteSpace: "nowrap" }}
					>
						{saving ? "Saving..." : "Save"}
					</button>
				</div>
			)}

			{isLoading ? (
				<div className="text-muted" style={{ fontSize: 12 }}>
					Loading templates...
				</div>
			) : isError ? (
				<div className="text-muted" style={{ fontSize: 12 }}>
					Unable to load geo templates.
				</div>
			) : templates.length > 0 ? (
				<div style={{ maxHeight: 120, overflowY: "auto" }}>
					{templates.map((t) => (
						<div
							key={t.id}
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								padding: "4px 0",
								borderBottom: "1px solid #f0f0f0",
							}}
						>
							<button
								type="button"
								className="btn btn-sm btn-link p-0 text-start text-decoration-none"
								onClick={() => handleLoad(t.id)}
								style={{ fontSize: 12, flex: 1 }}
							>
								{t.name}
							</button>
							<button
								type="button"
								className="btn btn-sm btn-link text-danger p-0"
								onClick={() => handleDelete(t.id)}
								style={{ fontSize: 11 }}
							>
								&times;
							</button>
						</div>
					))}
				</div>
			) : (
				<div className="text-muted" style={{ fontSize: 12 }}>
					No saved templates yet.
				</div>
			)}
		</div>
	);
};
